import Offer from "../../models/Offer";
import { OfferTemplate } from "../../models/OfferTemplate";
import Cookies from 'js-cookie';
import { apis } from "../../backend";
import OfferEvent from "../../models/OfferEvent";
import { Country } from "../../models/Country";
import { Theme } from "../../utils/theme";

//  See s2-offers.
const versionIdentifiers = [[], ['C', 'G', 'O', 'F', 'A']];

function decodePublicOfferIdV2(publicOfferId: string): { country: Country; } {
  //  Remove version.
  const countryUpperCase = publicOfferId.substring(3, 5);

  return {
    country: countryUpperCase.toLowerCase() as Country
  }
}

export function decodePublicOfferId(publicOfferId: string): { country?: Country; } | null {
  if (typeof publicOfferId !== 'string' || publicOfferId.length === 10) {
    //  Old, and not encoded.
    return null;
  }

  if (publicOfferId.length === 9) {
    //  Version 1 has 9 characters.
    return null;
  }

  const version = versionIdentifiers.findIndex(identifiers =>
    identifiers.find(identifier => publicOfferId.startsWith(identifier))) + 1;

  console.log("PublicId version: %d", version);

  if (!version) {
    console.warn("Uknown publicId version: %s", publicOfferId);
    return null;
  }

  if (version === 2) {
    return decodePublicOfferIdV2(publicOfferId);
  }

  return null;
}

/**
 * Fetches the offer by its public ID.
 * @param publicId The offer's public ID.
 * @param information Set of properties to limit the response to, for optimization.
 */
export async function getLinkedOffer(publicId: string): Promise<{ offer: Offer; template: OfferTemplate; theme?: DeepPartial<Theme> }> {
  const url = new URL(document.location.href);

  const { offer, template, theme } = await apis.offers.get(`/offers-public-v2/${publicId}`, {
    read: !window.location.search.includes('read=no'),
    printToken: url.searchParams.get('printToken') // Used by document service to create print.
  });
  return { offer: new Offer(offer), template: new OfferTemplate(template), theme };
}

/**
 * Requests a decline token.
 * The token is saved to the user's cookies and not returned.
 */
export async function requestDeclineToken(offerId: string) {
  Cookies.remove('s2_declineToken');
  await apis.offers.post(`/offer-responses-v1/${offerId}/decline-token`);
}

/**
 * Use this for getting a session ID.
 * If the session ID is set, use `createOfferEventAsync`!
 */
export async function createOfferEvent(offerId: string, event: OfferEvent): Promise<{ eventId: string; sessionId?: string; }> {
  if (isPrint()) {
    return { eventId: null, sessionId: null };
  }

  return apis.offers.post(`/offer-events-v2/${offerId}`, event);
}

export function createOfferEventAsync(offerId: string, event: OfferEvent) {
  if (navigator.sendBeacon) {
    navigator.sendBeacon(apis.offers.getUrl(
      `/offer-events-v2/${offerId}`),
      new Blob([JSON.stringify(event)], { type: 'application/json' })
    );
  } else {
    createOfferEvent(offerId, event);
  }
}

const { searchParams } = new URL(document.location.href);
let _print: boolean = !!(searchParams.get("print") || searchParams.get("printToken"));

/**
 * @returns Whether the print version of the offer is requested.
 */
export function isPrint() {
  return _print;
}
